'use client';
import { RefObject, useId, useEffect, useRef } from 'react';
import { Button, AppImage } from '@/app/common';
import { VideoPlayer } from '../ui/VideoPlayer';
import { Thumbnail } from '../ui/Thumbnail';
import { InfoCard } from '../ui/InfoCard';
import { Thumbnail as ThumbnailType, ButtonType } from '@workspace/interfaces';
import { VIDEO_VARIANTS } from '@workspace/constants';
import { sanityImageSrc } from '@/sanity/lib/image';
import { cn, getButtonVariant } from '@workspace/lib';

interface ImageVideoWithTextProps {
  wrapperRef: (node?: Element | null) => void;
  playerContainerRef: RefObject<HTMLDivElement | null>;
  thumbnail?: ThumbnailType;
  heading?: string | null;
  description?: string | null;
  buttons?: ButtonType[] | null;
  isBackgroundVideo: boolean;
  isPlaying?: boolean;
  onPlay: () => void;
  wrapperClassName?: string;
  shouldRenderOverlay?: boolean;
  aspectRatio?: string;
}

export function ImageVideoWithText({
  wrapperRef,
  playerContainerRef,
  thumbnail,
  heading,
  description,
  buttons,
  isBackgroundVideo,
  isPlaying = false,
  onPlay,
  wrapperClassName,
  shouldRenderOverlay = true,
  aspectRatio,
}: ImageVideoWithTextProps) {
  const hasHeading = typeof heading === 'string' && heading.trim() !== '';
  const hasDescription = typeof description === 'string' && description.trim() !== '';
  const hasButtons = Array.isArray(buttons) && buttons.length > 0;
  const hasContent = hasHeading || hasDescription || hasButtons;
  const uniqueId = useId().replace(/:/g, '-');
  const containerRef = useRef<HTMLDivElement>(null);
  const videoWrapperRef = useRef<HTMLDivElement>(null);

  // Determine if this is image-only mode (no video, just image)
  const isImageOnly = !isBackgroundVideo && !!thumbnail;

  // Split background class
  const bgClasses =
    wrapperClassName
      ?.split(' ')
      .filter((cls) => cls.startsWith('bg-'))
      .join(' ') || '';
  const otherClasses =
    wrapperClassName
      ?.split(' ')
      .filter((cls) => !cls.startsWith('bg-'))
      .join(' ') || '';

  // Mobile: 9:16, Desktop: use aspectRatio prop if available (for background videos)
  const shouldApplyDesktopAspectRatio = isBackgroundVideo && aspectRatio;

  // Parse video aspect ratio
  const videoAspectRatio = (() => {
    if (!isBackgroundVideo || !aspectRatio) return null;
    const match = aspectRatio.match(/(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/);
    if (!match) return null;
    return parseFloat(match[1]) / parseFloat(match[2]);
  })();

  // Apply cover sizing on mobile using wrapper transform
  useEffect(() => {
    if (!isBackgroundVideo || !videoAspectRatio || !containerRef.current || !videoWrapperRef.current) return;

    const applyCover = () => {
      const container = containerRef.current;
      const videoWrapper = videoWrapperRef.current;
      if (!container || !videoWrapper) return;

      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      if (!isMobile) {
        // Desktop: reset wrapper styles
        videoWrapper.style.removeProperty('transform');
        videoWrapper.style.removeProperty('width');
        videoWrapper.style.removeProperty('height');
        videoWrapper.style.removeProperty('left');
        videoWrapper.style.removeProperty('top');
        return;
      }

      const containerRect = container.getBoundingClientRect();
      if (containerRect.width === 0 || containerRect.height === 0) return;

      const containerAspectRatio = containerRect.width / containerRect.height;

      // Calculate scale needed to cover container completely
      // Start with video filling container width, then scale up if needed
      const baseWidth = containerRect.width;
      const baseHeight = baseWidth / videoAspectRatio;

      let scale: number;
      if (videoAspectRatio > containerAspectRatio) {
        // Video is wider - fit to width, but need to scale height to cover
        // Scale = containerHeight / videoHeight
        scale = containerRect.height / baseHeight;
      } else {
        // Video is taller - fit to height, but need to scale width to cover
        // Scale = containerWidth / videoWidth
        const baseHeightForFit = containerRect.height;
        const baseWidthForFit = baseHeightForFit * videoAspectRatio;
        scale = containerRect.width / baseWidthForFit;
      }

      // Ensure scale is at least 1 to cover container (no gaps)
      scale = Math.max(scale, 1);

      // Apply transform scale to wrapper
      // Position wrapper at container center, then scale it up
      videoWrapper.style.position = 'absolute';
      videoWrapper.style.left = '50%';
      videoWrapper.style.top = '50%';
      videoWrapper.style.transform = `translate(-50%, -50%) scale(${scale})`;
      videoWrapper.style.width = `${baseWidth}px`;
      videoWrapper.style.height = `${baseHeight}px`;
      videoWrapper.style.overflow = 'hidden';
    };

    applyCover();
    const resizeObserver = new ResizeObserver(applyCover);
    resizeObserver.observe(containerRef.current);

    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleChange = () => applyCover();
    mediaQuery.addEventListener('change', handleChange);
    window.addEventListener('resize', handleChange);

    return () => {
      resizeObserver.disconnect();
      mediaQuery.removeEventListener('change', handleChange);
      window.removeEventListener('resize', handleChange);
    };
  }, [isBackgroundVideo, videoAspectRatio]);

  return (
    <>
      {/* Style tag for desktop aspect ratio and mobile video cover  ( using style to overwrite the aspect ratio in the media query)*/}
      {isBackgroundVideo && (
        <style>{`
          @media (min-width: 992px) {
            .image-video-with-text-${uniqueId} {
              ${aspectRatio ? `aspect-ratio: ${aspectRatio} !important;` : ''}
            }
          }
        `}</style>
      )}
      <div
        ref={containerRef}
        className={cn(
          'relative w-full overflow-hidden',
          // Image-only: fixed heights, Video: aspect ratio
          isImageOnly ? 'h-[675px] md:h-[810px] min-h-0!' : 'aspect-9/16',
          shouldApplyDesktopAspectRatio && `image-video-with-text-${uniqueId}`,
          isBackgroundVideo && `mobile-video-cover-${uniqueId}`,
          otherClasses,
        )}
      >
        {/* Background layer */}
        <div
          className={cn(
            isBackgroundVideo || isImageOnly ? 'absolute inset-0 w-full h-full' : '',
            'overflow-hidden',
            bgClasses,
          )}
        >
          {isBackgroundVideo ? (
            <div
              ref={videoWrapperRef}
              className="absolute inset-0 w-full h-full"
              style={{
                transformOrigin: 'center center',
              }}
            >
              <VideoPlayer
                containerRef={playerContainerRef}
                onToggle={() => {
                  // No-op: clicks handled by Vimeo controls
                }}
                enableClickHandler={false}
              />
              {/* Optional thumbnail overlay for background video - hide when playing */}
              {thumbnail && !isPlaying && <Thumbnail thumbnail={thumbnail} onPlay={onPlay} />}
            </div>
          ) : (
            isImageOnly &&
            thumbnail?.url && (
              <AppImage
                fill
                src={sanityImageSrc(thumbnail, {
                  width: 1920,
                  height: undefined,
                })}
                alt={thumbnail.alt ?? 'Image'}
                className="w-full h-full object-cover"
                sanityImage={thumbnail}
              />
            )
          )}
        </div>

        {/* Content layer */}
        <div
          className={cn(
            'absolute inset-0 px-md py-2xl md:p-4xl flex flex-col justify-end h-full pointer-events-none',
            'porta-container z-20',
          )}
          ref={wrapperRef}
        >
          {hasContent && (
            <InfoCard
              variant={VIDEO_VARIANTS.ImageVideoWithText}
              heading={heading ?? ''}
              description={description ?? ''}
              wrapperClassName="md:!gap-xl !gap-md pointer-events-auto"
              shouldRender={shouldRenderOverlay}
            >
              <div className="flex gap-xs flex-wrap flex-col md:flex-row md:gap-md">
                {Array.isArray(buttons) &&
                  buttons.map((btn, idx) => (
                    <Button
                      variant={getButtonVariant(btn.style)}
                      key={idx}
                      url={btn.url ?? '#'}
                      openInNewTab={btn.linkType === 'external' ? true : btn.isOpenInNewTab}
                      className="w-full md:w-auto md:max-w-60 md:min-w-5xl font-semibold px-sm py-button"
                    >
                      {btn.label}
                    </Button>
                  ))}
              </div>
            </InfoCard>
          )}
        </div>
      </div>
    </>
  );
}
